import { useLayoutEffect, useMemo, useRef } from "react";
import {
  useZoneEditorStore,
  type ZoneMapping,
} from "../../state/zone-editor-store.js";
import "./ZoneFieldList.css";

/**
 * R-125 (5.4 + 5.6 + 5.7 + 5.8) — Right-panel field list for the Zones tab.
 *
 * - Pulls labels live from `useZoneEditorStore.zoneMappings`, which the
 *   parent keeps in sync with the form-config draft via `syncFieldDefinitions`.
 *   This fixes the "all fields labelled 'New Field'" bug (#2) and the
 *   "list empty after reload" bug (#6).
 *
 * - Splits fields into OCR-extracted (top) and Keying guides (bottom).
 *   OCR rows wear the copper accent + matching tint; guide rows wear the
 *   muted slate accent + matching tint.
 *
 * - Clicking a row enters draw mode for that field directly (no separate
 *   Draw toolbar toggle). The next mousedown+drag on the canvas commits.
 *
 * - Rows reflect completion state via `data-zone-state="complete"` when
 *   a zone is present, so the operator sees at-a-glance which fields are
 *   done vs pending.
 */
export function ZoneFieldList() {
  const zoneMappings = useZoneEditorStore((s) => s.zoneMappings);
  const selectedZoneFieldId = useZoneEditorStore((s) => s.selectedZoneFieldId);
  const toolMode = useZoneEditorStore((s) => s.toolMode);
  const selectFieldForDraw = useZoneEditorStore((s) => s.selectFieldForDraw);
  const selectZone = useZoneEditorStore((s) => s.selectZone);
  const removeZone = useZoneEditorStore((s) => s.removeZone);
  const updateZoneProps = useZoneEditorStore((s) => s.updateZoneProps);

  const { ocrFields, guideFields } = useMemo(() => {
    const ocrFields: ZoneMapping[] = [];
    const guideFields: ZoneMapping[] = [];
    for (const zm of zoneMappings) {
      if (zm.ocrExtracted) ocrFields.push(zm);
      else guideFields.push(zm);
    }
    return { ocrFields, guideFields };
  }, [zoneMappings]);

  // R-173 (2026-05-26) — Preserve scroll position across re-renders.
  // The Zones tab autosave debounces 600ms after a draw and mutates
  // `zoneMappings` on each flush, which re-renders the whole list
  // and (without intervention) resets the operator's scroll back to
  // the top while they are mid-draw on a neighbouring field. We
  // capture scrollTop right before commit and write it back right
  // after, so the operator never loses their place.
  //
  // useLayoutEffect runs synchronously after DOM mutation but BEFORE
  // the browser paints — that's the only hook timing that can both
  // see the current DOM state and patch it before the user notices.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const savedScrollRef = useRef(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // On re-render, the layout effect runs AFTER React has flushed
    // the new DOM but before paint. If the browser has already reset
    // scrollTop (which it does when children are remounted or when
    // the list height shrinks), we restore it; otherwise this is a
    // no-op.
    if (savedScrollRef.current > 0 && el.scrollTop !== savedScrollRef.current) {
      el.scrollTop = savedScrollRef.current;
    }
  });
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    savedScrollRef.current = el.scrollTop;
  };

  const ocrDoneCount = ocrFields.filter((f) => f.zone !== null).length;
  const guideDoneCount = guideFields.filter((f) => f.zone !== null).length;

  const renderRow = (zm: ZoneMapping, klass: "ocr" | "guide") => {
    const isSelected = selectedZoneFieldId === zm.fieldDefinitionId;
    const isDrawing = isSelected && toolMode === "draw" && !zm.zone;
    const state: "complete" | "drawing" | "pending" =
      zm.zone !== null ? "complete" : isDrawing ? "drawing" : "pending";

    return (
      <li
        key={zm.fieldDefinitionId}
        className="zfl-row"
        data-zone-class={klass}
        data-zone-state={state}
        data-selected={isSelected ? "true" : undefined}
      >
        <button
          type="button"
          className="zfl-row-main"
          onClick={() => {
            // R-125 (5.6) — clicking a row that already has a zone selects
            // it (so the operator can resize/move/delete via the canvas).
            // Clicking an empty row enters draw mode for that field.
            if (zm.zone) {
              selectZone(zm.fieldDefinitionId);
            } else {
              selectFieldForDraw(zm.fieldDefinitionId);
            }
          }}
        >
          <span className="zfl-row-label">{zm.fieldLabel}</span>
          <span className="zfl-row-status" aria-hidden="true">
            {state === "complete"
              ? "Drawn"
              : state === "drawing"
                ? "Drawing…"
                : "Tap to draw"}
          </span>
        </button>
        {zm.zone && (
          <button
            type="button"
            className="zfl-row-clear"
            onClick={(e) => {
              e.stopPropagation();
              removeZone(zm.fieldDefinitionId);
            }}
            aria-label={`Clear zone for ${zm.fieldLabel}`}
            title="Clear zone"
          >
            ×
          </button>
        )}
        {/* Marksense v2 + Handwriting Phase 3 — per-zone properties strip on
            the SELECTED drawn row. Props live ON the zone object, so the
            existing wholesale zone save path persists them unchanged. */}
        {isSelected && zm.zone && (
          <div className="zfl-zone-props">
            <label className="zfl-prop-check">
              <input
                type="checkbox"
                checked={zm.zone.isHandwriting === true}
                onChange={(e) =>
                  updateZoneProps(zm.fieldDefinitionId, {
                    isHandwriting: e.target.checked ? true : undefined,
                  })
                }
                aria-label={`Handwriting zone for ${zm.fieldLabel}`}
              />
              Handwriting
            </label>
            <label className="zfl-prop-row">
              <span>Mark kind</span>
              <select
                value={zm.zone.markKind ?? ""}
                aria-label={`Mark kind for ${zm.fieldLabel}`}
                onChange={(e) => {
                  const kind = e.target.value as
                    | ""
                    | "Checkbox"
                    | "Bubble"
                    | "Signature"
                    | "AnyMark";
                  updateZoneProps(
                    zm.fieldDefinitionId,
                    kind === ""
                      ? // None ⇒ not a mark zone; a dangling group ref would
                        // fail the publish gate, so clear it too.
                        { markKind: undefined, markGroupId: undefined }
                      : { markKind: kind },
                  );
                }}
              >
                <option value="">None</option>
                <option value="Checkbox">Checkbox</option>
                <option value="Bubble">Bubble</option>
                <option value="Signature">Signature</option>
                <option value="AnyMark">Any mark</option>
              </select>
            </label>
            {zm.zone.markKind === "Checkbox" && (
              <p className="zfl-prop-hint">
                Respondents circle their answers? Use <strong>Any mark</strong> —
                Checkbox frame-suppression can erase a drawn ring along with the
                printed box outline.
              </p>
            )}
            {zm.zone.markKind && (
              <label className="zfl-prop-row">
                <span>Mark group</span>
                <input
                  type="text"
                  value={zm.zone.markGroupId ?? ""}
                  placeholder="none"
                  aria-label={`Mark group for ${zm.fieldLabel}`}
                  onChange={(e) =>
                    updateZoneProps(zm.fieldDefinitionId, {
                      markGroupId: e.target.value.trim() === "" ? undefined : e.target.value.trim(),
                    })
                  }
                />
              </label>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <aside className="zfl" aria-label="Field zones">
      <div className="zfl-scroll" ref={scrollRef} onScroll={handleScroll}>
        <section className="zfl-section" data-zone-class="ocr">
          <header className="zfl-section-header">
            <h3 className="zfl-section-title">OCR Extracted</h3>
            <span className="zfl-section-count">
              {ocrDoneCount}/{ocrFields.length}
            </span>
          </header>
          {ocrFields.length === 0 ? (
            <p className="zfl-empty">No fields selected for OCR extraction.</p>
          ) : (
            <ul className="zfl-list">{ocrFields.map((f) => renderRow(f, "ocr"))}</ul>
          )}
        </section>

        <section className="zfl-section" data-zone-class="guide">
          <header className="zfl-section-header">
            <h3 className="zfl-section-title">Keying Guides</h3>
            <span className="zfl-section-count">
              {guideDoneCount}/{guideFields.length}
            </span>
          </header>
          {guideFields.length === 0 ? (
            <p className="zfl-empty">All fields are OCR-extracted.</p>
          ) : (
            <ul className="zfl-list">
              {guideFields.map((f) => renderRow(f, "guide"))}
            </ul>
          )}
        </section>
      </div>
    </aside>
  );
}
